/* Video Embed Handler
 * Vanilla JS replacement for jQuery iframe code
 * Handles YouTube, Vimeo, and other embedded videos with accessibility and performance improvements
 */

class VideoEmbedHandler {
  constructor() {
    this.initVideoEmbeds();
  }

  initVideoEmbeds() {
    const iframes = document.querySelectorAll('iframe:not([data-video-processed])');
    iframes.forEach(this.wrapIframe.bind(this));
  }

  /**
   * Determine whether an iframe is an actual video embed (as opposed to
   * consent-management/CMP utility frames like the IAB GPP/TCF/USP
   * "locator" iframes, which are hidden and have no src)
   * @param {HTMLIFrameElement} iframe
   * @returns {boolean}
   */
  isVideoIframe(iframe) {
    const name = iframe.getAttribute('name') || '';
    if (/^__(gpp|tcfapi|uspapi)Locator$/i.test(name)) return false;

    const src = iframe.src || iframe.getAttribute('data-src') || '';
    if (!src) return false;

    return /youtube|vimeo|tiktok|instagram|twitter|x\.com/i.test(src);
  }

  /**
   * Wrap individual iframe with modern video embed structure
   * @param {HTMLIFrameElement} iframe
   * @param {number} index
   */
  wrapIframe(iframe, index) {
    if (iframe.hasAttribute('data-video-processed')) return;
    if (!this.isVideoIframe(iframe)) return;

    iframe.setAttribute('data-video-processed', 'true');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('data-video-id', index.toString());

    // Check if iframe is within rich text editor content (Shopify RTE or article content)
    const isInRichText = iframe.closest(
      '.rte, .richtext, [class*="rich-text"], [class*="rte"], .article-content__content, .article-template__content'
    );

    const wrapper = document.createElement('div');
    wrapper.className = 'video-embed-wrapper';
    wrapper.setAttribute('data-video-index', index.toString());

    const src = iframe.src || iframe.getAttribute('data-src') || '';
    this.setVideoAspectRatio(wrapper, src);

    iframe.parentNode.insertBefore(wrapper, iframe);
    wrapper.appendChild(iframe);

    // Only create overlay and add event listeners if NOT in rich text content
    if (!isInRichText) {
      const overlay = this.createOverlay(src);
      wrapper.appendChild(overlay);
      this.attachEventListeners(wrapper, overlay);
    }
  }

  /**
   * Set appropriate aspect ratio based on video source
   * @param {HTMLElement} wrapper
   * @param {string} src
   */
  setVideoAspectRatio(wrapper, src) {
    if (src.includes('youtube') || src.includes('vimeo')) {
      // Keep default 16:9 ratio
      return;
    } else if (src.includes('tiktok') || src.includes('instagram')) {
      wrapper.classList.add('video-embed-wrapper--portrait');
    } else if (src.includes('twitter') || src.includes('x.com')) {
      wrapper.classList.add('video-embed-wrapper--square');
    }
  }

  /**
   * Create video overlay with play button
   * @param {string} src
   * @returns {HTMLElement}
   */
  createOverlay(src) {
    const overlay = document.createElement('div');
    overlay.className = 'video-overlay';
    overlay.setAttribute('role', 'button');
    overlay.setAttribute('aria-label', 'Play embedded video');
    overlay.setAttribute('tabindex', '0');

    let platform = 'video';
    if (src.includes('youtube')) platform = 'YouTube video';
    else if (src.includes('vimeo')) platform = 'Vimeo video';
    else if (src.includes('tiktok')) platform = 'TikTok video';

    overlay.innerHTML = `
      <svg class="video-play-button" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M8 5v14l11-7z"/>
      </svg>
      <span class="sr-only">Click to play ${platform}</span>
    `;

    return overlay;
  }

  /**
   * Attach event listeners to video wrapper
   * @param {HTMLElement} wrapper
   * @param {HTMLElement} overlay
   */
  attachEventListeners(wrapper, overlay) {
    overlay.addEventListener('click', () => this.activateVideo(wrapper));

    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.activateVideo(wrapper);
      }
    });

    this.observeVideo(wrapper);
  }

  /**
   * Activate video by removing overlay
   * @param {HTMLElement} wrapper - The video wrapper element
   */
  activateVideo(wrapper) {
    const overlay = wrapper.querySelector('.video-overlay');
    if (!overlay) return;

    wrapper.classList.add('video-embed-wrapper--activated');

    overlay.style.opacity = '0';
    overlay.style.pointerEvents = 'none';

    setTimeout(() => {
      if (overlay.parentNode) {
        overlay.remove();
      }
    }, 300);

    const iframe = wrapper.querySelector('iframe');
    if (iframe) {
      iframe.focus();

      if (iframe.src.includes('youtube')) {
        this.enableYouTubeAutoplay(iframe);
      }
    }

    // Dispatch custom event for analytics
    wrapper.dispatchEvent(
      new CustomEvent('videoActivated', {
        detail: {
          src: iframe.src,
          index: wrapper.getAttribute('data-video-index'),
        },
      })
    );
  }

  /**
   * Enable autoplay for YouTube videos when activated
   * @param {HTMLIFrameElement} iframe
   */
  enableYouTubeAutoplay(iframe) {
    const src = iframe.src;
    if (src.includes('autoplay=0')) {
      iframe.src = src.replace('autoplay=0', 'autoplay=1');
    } else if (!src.includes('autoplay=')) {
      const separator = src.includes('?') ? '&' : '?';
      iframe.src = `${src}${separator}autoplay=1`;
    }
  }

  /**
   * Add intersection observer for performance
   * @param {HTMLElement} wrapper
   */
  observeVideo(wrapper) {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('video-embed-wrapper--in-view');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px 0px',
      }
    );

    observer.observe(wrapper);
  }

  refresh() {
    this.initVideoEmbeds();
  }
}

function initVideoEmbeds() {
  if (typeof window.videoEmbedHandler === 'undefined') {
    window.videoEmbedHandler = new VideoEmbedHandler();
  } else {
    window.videoEmbedHandler.refresh();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoEmbeds);
} else {
  initVideoEmbeds();
}

document.addEventListener('shopify:section:load', initVideoEmbeds);

document.addEventListener('shopify:section:reorder', initVideoEmbeds);

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VideoEmbedHandler;
}
